import { observer } from "mobx-react";
import React, { useRef, useState, useEffect, useContext } from "react";
import AnnotationRect from "../annotation-rect";
import { ViralVariantMarker } from "../viral-variant-marker";
import { ViralTrackScrubber } from "../viral-track-scrubber";
import { Annotation, GenomicVariant } from "../../api";
import { StoresContext } from "../../pages/_app";

const SCALE = 0.08;

interface Props {
  annotationSetUuid: string;
  assemblyUuid?: string;
}

export const InnerViralTrack = observer((props: Props) => {
  const rootStore = useContext(StoresContext);
  const { annotationStore, viralVariantStore } = rootStore;

  const { annotationSetUuid } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [scrubberWidthPercentage, setScrubberWidthPercentage] = useState(0);
  const [currentScrollPosition, setCurrentScrollPosition] = useState(0);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    container.addEventListener("scroll", handleScroll);
    updateScrubberWidth(container.getBoundingClientRect().width);

    return () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      container.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (viralVariantStore.needsTrackJump) {
      jumpToVariant(viralVariantStore.selectedVariant!);
      viralVariantStore.trackJumpDone();
    }
  }, [viralVariantStore.needsTrackJump]);

  function updateScrubberWidth(containerWidth: number) {
    const svg = svgRef.current;
    if (svg && scrubberWidthPercentage === 0) {
      const svgWidth = svg.getBoundingClientRect().width;
      let scrubberWidthPercentage = (containerWidth / svgWidth) * 100;
      if (scrubberWidthPercentage > 100) {
        // this rect determines the screen width for the viral store
        scrubberWidthPercentage = 0;
        viralVariantStore.setCanScroll(false);
      }
      setScrubberWidthPercentage(scrubberWidthPercentage);
    }
  }

  function handleScroll() {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    setCurrentScrollPosition(container.scrollLeft);
  }

  function jumpToPosition(offsetX: number, genomeLength: number): void {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const trackWidth = container.getBoundingClientRect().width;
    const annotationLoc = (offsetX / trackWidth) * genomeLength;
    container.scrollLeft = annotationLoc * SCALE;
  }

  function jumpToVariant(variant: GenomicVariant) {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    // center the jumped-to annotation on the track
    const halfScreen = container.offsetWidth / 2;
    const variantLength = variant.absolute_end_loc - variant.absolute_start_loc;
    const halfVariant = (variantLength / 2) * SCALE;
    container.scrollLeft = SCALE * variant.absolute_start_loc - halfScreen + halfVariant;
  }

  function selectAnnotation(annotation: Annotation) {
    annotationStore.selectAnnotation(annotation);
  }

  function selectVariant(variant: GenomicVariant) {
    viralVariantStore.selectVariant(variant);
  }

  function renderContigPositions(contigPositions: number[]) {
    const positions = [];
    let runningSum = 0;
    for (let i = 0; i < contigPositions.length; i++) {
      runningSum += contigPositions[i];
      positions.push(
        <line
          x1={runningSum * SCALE}
          x2={runningSum * SCALE}
          y1={2}
          y2={16}
          stroke="#189493"
          strokeOpacity="0.7"
          strokeWidth="3"
          strokeLinecap="round"
          key={annotationSetUuid + "-" + runningSum}
        />
      );
    }
    return positions;
  }

  const metadata = annotationStore.annotations.metadata[props.annotationSetUuid]!;
  const genomeLength = metadata.derived_metadata.length;
  const contigPositions = metadata.derived_metadata.contig_lengths;

  const annotations = annotationStore.annotations.data.map((item) => {
    return (
      <AnnotationRect
        selected={annotationStore.selectedUuid === item.uuid}
        annotation={item}
        scale={SCALE}
        key={item.uuid}
        isVirus={true}
        isObfuscated={false}
        onClick={selectAnnotation}
        scrollLeftPosition={SCALE * item.absolute_start_loc}
      />
    );
  });

  const variants =
    viralVariantStore.searchResults[viralVariantStore.selectedVariantRunUuid!].results || [];
  const variantMarkers = variants.map((variant, idx) => {
    return (
      <ViralVariantMarker
        selected={false}
        variant={variant}
        scale={SCALE}
        key={idx}
        onClick={selectVariant}
        scrollLeftPosition={SCALE * variant.absolute_start_loc}
      />
    );
  });
  return (
    <div className="annotation-track">
      <div className="annotation-track__container">
        <ViralTrackScrubber
          genomeLength={genomeLength}
          contigPositions={contigPositions}
          windowWidthPercentage={scrubberWidthPercentage}
          windowBeginsPercentage={((currentScrollPosition / genomeLength) * 100) / SCALE}
          jumpToPosition={jumpToPosition}
          jumpToVariant={jumpToVariant}
        />
        <br />
        <div className="annotation-track__track" ref={containerRef}>
          <svg ref={svgRef} width={genomeLength * SCALE} height={40}>
            {annotations}
            {variantMarkers}
            {renderContigPositions(contigPositions)}
          </svg>
        </div>
      </div>
    </div>
  );
});
